<template>
  <g>
    <g ref="chart">
      <path
        v-for="(arc, index) in arcs"
        :key="index"
        :fill="arc.fill"
        :opacity="arcOpacity(index)"
        @mouseover="onMouseover(arc, index)"
        @mouseleave="onMouseleave()"
      ></path>
    </g>
    <slot/>
  </g>
</template>

<script>
import { pie, interpolate, arc, select } from 'd3';

export default {
  props: {
    // Custom to Chart Wrapper
    data: {
      type: Array,
      required: true,
    },
    radius: {
      type: Number,
      required: true,
    },
    // Custom to Pie Chart
    isDonut: {
      type: Boolean,
      default: true,
    },
    wrapperWidth: {
      type: Number,
      required: true,
    },
    wrapperHeight: {
      type: Number,
      required: true,
    },
    hasLabels: {
      type: Boolean,
      default: false,
    },
    hasOuterLabels: {
      type: Boolean,
      default: false,
    },
    hasTooltip: {
      type: Boolean,
      default: false,
    },
    strokeWidth: {
      type: Number,
      default: 0,
    },
    paddingAngle: {
      type: Number,
      default: 0,
    },
    transitionSpeed: {
      type: Number,
      default: 1000,
    },
  },
  data() {
    return {
      lineLengthPercent: 0.2,
      lineBreakPlacement: 0.9,
      hoveredIndex: null,
    };
  },
  computed: {
    adjustedRadius() {
      return this.hasOuterLabels
      // To make room for outer labels, make radius larger
        ? this.radius / (1 - this.lineLengthPercent)
        : this.radius;
    },
    innerRadius() {
      return this.isDonut ? this.radius - this.strokeWidth : 0;
    },
    chartGenerator() {
      return pie()
        .startAngle(0)
        .endAngle(2 * Math.PI)
        .value(d => d.value)
        .padAngle(this.paddingAngle);
    },
    chartData() {
      const filteredData = this.data.filter(d => d.value !== 0);

      return this.chartGenerator(filteredData);
    },
    angleInterpolation() {
      return interpolate(this.chartGenerator.startAngle()(), this.chartGenerator.endAngle()());
    },
    arcGenerator() {
      return this.hasOuterLabels
        ? this.innerArcGenerator
        : this.standardArcGenerator;
    },
    standardArcGenerator() {
      return arc()
        .innerRadius(this.innerRadius)
        .outerRadius(this.adjustedRadius);
    },
    innerArcGenerator() {
      return arc()
        .innerRadius(this.innerRadius)
        .outerRadius(this.radius);
    },
    outerArcGenerator() {
      return arc()
        .innerRadius(this.adjustedRadius * this.lineBreakPlacement)
        .outerRadius(this.adjustedRadius * this.lineBreakPlacement);
    },
    arcs() {
      return this.chartData.map(d => {

        return {
          d,
          fill: d.data.color,
          tooltipPosition: this.getTooltipPosition(d.startAngle, d.endAngle),
          hasData: d.data.label &&
            d.data.displayValue &&
            d.data.metric &&
            d.data.metric.key,
        };
      });
    },
  },
  methods: {
    getTooltipPosition(startAngle, endAngle) {
      const angle = endAngle - startAngle;
      let tooltipPosition;

      if (angle >= Math.PI) {
        // if arc is more than 50% of chart
        tooltipPosition = this.placeTooltipCloseToCenter();
      } else {
        tooltipPosition = this.placeTooltipCloseToSection(startAngle, endAngle);
      }

      return {
        left: this.wrapperWidth / 2 + tooltipPosition.left,
        top: this.wrapperHeight / 2 + tooltipPosition.top,
        fromCorner: tooltipPosition.fromCorner,
      };
    },
    getCoordinatesPosition(radius, angle) {
      return [
        radius * (Math.sin(angle)),
        -radius * (Math.cos(angle)),
      ];
    },
    placeTooltipCloseToCenter() {
      const left = -0.2 * this.radius;
      const top = left;
      const fromCorner = {
        horizontal: 'right',
        vertical: 'bottom',
      };

      return { left, top, fromCorner };
    },
    placeTooltipCloseToSection(startAngle, endAngle) {
      let left, top, horizontal, vertical;

      // place one of the tooltip corners on
      if (this.isDonut) {
        // the middle of the two inner cornerRadius of the angle
        const [x1, y1] = this.getCoordinatesPosition(this.innerRadius, startAngle);
        const [x2, y2] = this.getCoordinatesPosition(this.innerRadius, endAngle);

        left = this.getTwoAvg(x1, x2);
        top = this.getTwoAvg(y1, y2);
        horizontal = left > 0 ? 'right' : 'left';
        vertical = top > 0 ? 'bottom': 'top';
      } else {
        // the middle of the edge of the startAngle
        const [x1, y1] = this.getCoordinatesPosition(this.radius, startAngle);
        const [x2, y2] = [0, 0];

        left = this.getTwoAvg(x1, x2);
        top = this.getTwoAvg(y1, y2);
        vertical = left > 0 ? 'bottom' : 'top';
        horizontal = top > 0 ? 'left' : 'right';
      }

      return {
        left,
        top,
        fromCorner: {
          horizontal,
          vertical,
        },
      };
    },
    getTwoAvg(x, y) {
      return (x + y) / 2;
    },
    drawArcs() {
      const arcNode = this.$refs.chart;

      select(arcNode)
        .selectAll('path')
        .data(this.arcs)
        .transition()
        .duration(this.transitionSpeed)
        .attr('d', this.arcGenerator)
        .attrTween('d', arc => {
          const d = arc.d;
          const originalEnd = d.endAngle;

          return transitionPoint => {
            const currentAngle = this.angleInterpolation(transitionPoint);

            if (currentAngle < d.startAngle) return '';

            d.endAngle = Math.min(currentAngle, originalEnd);

            return this.arcGenerator(d);
          };
        });
    },
    getMidAngle(d) {
      return d.startAngle + (d.endAngle - d.startAngle)/2;
    },
    isRightPositioned(d) {
      // flip position of label for the last arc if it's small to stop text collisions
      const tenPercentOfCircle = 0.6;

      if (
        d.endAngle >= Math.PI * 2
        && d.endAngle - d.startAngle < tenPercentOfCircle
      ) return true;

      // otherwise position normally
      return this.getMidAngle(d) < Math.PI;
    },
    addLables() {
      const arcNode = this.$refs.chart;

      select(arcNode)
        .selectAll('arc')
        .data(this.arcs)
        .enter()
        .append('text')
        .text(arc => arc.d.data.displayValue)
        .attr('transform', arc => 'translate(' + this.arcGenerator.centroid(arc.d) + ')')
        .style('font-size', '16px')
        .style('text-anchor', 'middle')
        .style('fill', 'white');
    },
    addOuterLabels() {
      const arcNode = this.$refs.chart;

      // Add text
      select(arcNode)
        .selectAll('arc')
        .data(this.arcs)
        .enter()
        .append('text')
        .attr('dy', '.35em') // center text to line
        .text(arc => arc.d.data.displayValue)
        .attr('transform', arc => {
          const d = arc.d;
          const position = this.outerArcGenerator.centroid(d);

          position[0] = this.adjustedRadius * 0.9 * (this.isRightPositioned(d) ? 1 : -1);

          return 'translate(' + position + ')';
        },
        )
        .style('font-size', '12px')
        .style('text-anchor', arc => {
          return this.isRightPositioned(arc.d) ? 'start' : 'end';
        },
        )
        .style('fill', '#223448');
      // Add lines
      select(arcNode)
        .selectAll('arc')
        .data(this.arcs)
        .enter()
        .append('polyline')
        .style('stroke-width', 1)
        .style('stroke', 'black')
        .style('fill', 'none')
        .attr('points', arc => {
          const d = arc.d;
          const position = this.outerArcGenerator.centroid(d);

          position[0] = this.adjustedRadius * 0.85 * (this.isRightPositioned(d) ? 1 : -1);

          return [
            this.arcGenerator.centroid(d),
            this.outerArcGenerator.centroid(d),
            position,
          ];
        },
        );
    },
    onMouseleave() {
      if (this.hasTooltip) {
        this.hoveredIndex = null;
        this.$emit('onLeave');
      }
    },
    onMouseover(arc, index) {
      if (this.hasTooltip && arc.hasData) {
        this.hoveredIndex = index;
        this.$emit('onHover', {
          data: arc.d.data,
          position: arc.tooltipPosition,
        });
      }
    },
    arcOpacity(index) {
      if (this.hoveredIndex === null) return 1;

      return this.hoveredIndex === index ? 1 : .25;
    },
  },
  mounted() {
    this.drawArcs();

    if (this.hasLabels) this.addLables();
    if (this.hasOuterLabels) this.addOuterLabels();
  },
};
</script>
